import { Box, Typography, Grid, Paper, useTheme, useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getQuickAccessItems } from "../../api/quickAccess";
import { getMuiIcon } from "../../utils/muiIconMap";
import { useLanguage } from "../../context/LanguageContext";

// Indian tricolor theme colors
const INDIA_SAFFRON = "#FF9933";
const INDIA_GREEN = "#138808";
const INDIA_BLUE = "#000080";

const QuickAccess = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [items, setItems] = useState([]);
  const { language } = useLanguage();

  useEffect(() => {
    fetchQuickAccess();
  }, [language]);

  const fetchQuickAccess = async () => {
    try {
      const data = await getQuickAccessItems(language);
      setItems(data);
    } catch (error) {
      console.error("Quick Access API error:", error);
    }
  };

  // Get gradient based on index for visual variety
  const getCardGradient = (index) => {
    const gradients = [
      `linear-gradient(135deg, ${INDIA_SAFFRON}15, ${INDIA_BLUE}10)`,
      `linear-gradient(135deg, ${INDIA_GREEN}15, ${INDIA_SAFFRON}10)`,
      `linear-gradient(135deg, ${INDIA_BLUE}15, ${INDIA_GREEN}10)`,
    ];
    return gradients[index % gradients.length];
  };

  return (
    <Box 
      sx={{ 
        py: { xs: 4, sm: 5, md: 6 }, 
        px: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "#f8fafc",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: `linear-gradient(90deg, ${INDIA_SAFFRON}, #ffffff, ${INDIA_GREEN})`,
        }
      }}
    >
      {/* Header Section with Tricolor Accent */}
      <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4, md: 5 } }}>
        <Typography 
          variant="h5" 
          sx={{
            fontWeight: 700,
            fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
            color: "#1e293b",
            position: "relative",
            display: "inline-block",
            "&:after": {
              content: '""',
              position: "absolute",
              bottom: -8,
              left: "50%",
              transform: "translateX(-50%)",
              width: { xs: 60, sm: 80 },
              height: 3,
              background: `linear-gradient(90deg, ${INDIA_SAFFRON}, ${INDIA_GREEN})`,
              borderRadius: 2,
            }
          }}
        >
          {language === "mr" ? "द्रुत प्रवेश" : "Quick Access"}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: "#64748b", 
            mt: 2,
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            maxWidth: 600,
            mx: "auto"
          }}
        >
          {language === "mr" 
            ? "महत्त्वाच्या सेवा आणि माहितीपर्यंत झटपट पोहोचा" 
            : "Quick access to important services and information"}
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
        {items.map((item, index) => {
          const IconComponent = getMuiIcon(item.iconName);
          const cardGradient = getCardGradient(index);

          return (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Paper
                elevation={0}
                onClick={() => navigate(item.redirectPath)}
                sx={{
                  p: { xs: 2, sm: 2.5, md: 3 },
                  height: { xs: 180, sm: 200, md: 170 }, // FIXED HEIGHT for all cards
                  background: cardGradient,
                  borderRadius: { xs: 2, sm: 2.5, md: 3 },
                  border: "1px solid rgba(255,255,255,0.8)",
                  backdropFilter: "blur(8px)",
                  cursor: "pointer",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "4px",
                    background: `linear-gradient(90deg, ${INDIA_SAFFRON}, ${INDIA_GREEN})`,
                    opacity: 0,
                    transition: "opacity 0.3s ease",
                  },
                  "&:hover": {
                    transform: { xs: "none", sm: "translateY(-4px)" },
                    boxShadow: "0 12px 24px -8px rgba(0,0,0,0.15)",
                    borderColor: "transparent",
                    "&::before": {
                      opacity: 1,
                    },
                    "& .icon-wrapper": {
                      transform: "scale(1.1) rotate(5deg)",
                    }
                  },
                }}
              >
                {/* Icon and Content - with flex column layout */}
                <Box sx={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  height: "100%",
                }}>
                  {/* Top section with icon and title */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    {/* Icon Wrapper */}
                    <Box 
                      className="icon-wrapper"
                      sx={{
                        width: { xs: 48, sm: 52, md: 56 },
                        height: { xs: 48, sm: 52, md: 56 },
                        borderRadius: { xs: 1.5, sm: 2 },
                        background: `linear-gradient(135deg, ${item.iconColor || INDIA_BLUE}20, #ffffff)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "transform 0.3s ease",
                        border: `2px solid ${item.iconColor || INDIA_BLUE}30`,
                        flexShrink: 0,
                      }}
                    >
                      {IconComponent && (
                        <IconComponent
                          sx={{ 
                            fontSize: { xs: 24, sm: 28, md: 32 }, 
                            color: item.iconColor || INDIA_BLUE,
                          }}
                        />
                      )}
                    </Box>

                    {/* Title */}
                    <Typography 
                      variant="h6" 
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
                        color: "#1e293b",
                        lineHeight: 1.3,
                        flex: 1,
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>

                  {/* Description - with fixed height and proper spacing */}
                  <Typography 
                    variant="body2" 
                    sx={{
                      color: "#475569",
                      fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                      lineHeight: 1.5,
                      mt: 1.5,
                      mb: 1,
                      flex: 1,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      minHeight: { xs: 32, sm: 36, md: 40 }, // Fixed height for description
                    }}
                  >
                    {item.description}
                  </Typography>

                  {/* Footer Indicator - always at bottom */}
                  <Box 
                    sx={{ 
                      display: "flex", 
                      justifyContent: "flex-end",
                      alignItems: "center",
                      mt: "auto", // Pushes to bottom
                      pt: 1,
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: item.iconColor || INDIA_BLUE,
                        fontWeight: 500,
                        fontSize: { xs: "0.6rem", sm: "0.65rem", md: "0.7rem" },
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      {language === "mr" ? "पहा →" : "View →"}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default QuickAccess;




// import { Box, Typography, Grid } from "@mui/material";
// import { useEffect, useState } from "react";
// import InfoCard from "../shared/InfoCard/InfoCard";
// import { useNavigate } from "react-router-dom";
// import { getQuickAccessItems } from "../../api/quickAccess";
// import { getMuiIcon } from "../../utils/muiIconMap";
// import { useLanguage } from "../../context/LanguageContext";

// const INDIA_SAFFRON = "#FF9933";
// const INDIA_GREEN = "#138808";
// const INDIA_BLUE = "#000080";

// const QuickAccess = () => {
//   const navigate = useNavigate();
//   const [items, setItems] = useState([]);
//   const { language } = useLanguage();

//   useEffect(() => {
//     fetchQuickAccess();
//   }, [language]);

//   const fetchQuickAccess = async () => {
//     try {
//       const data = await getQuickAccessItems(language);
//       setItems(data);
//     } catch (error) {
//       console.error("Quick Access API error:", error);
//     }
//   };

//   return (
//     <Box sx={{ py: 6, px: { xs: 2, md: 4 } }}>
//       <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4, md: 5 } }}>
//         <Typography
//           variant="h5"
//           sx={{
//             fontWeight: 700,
//             fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
//             color: "#1e293b",
//             position: "relative",
//             display: "inline-block",
//             "&:after": {
//               content: '""',
//               position: "absolute",
//               bottom: -8,
//               left: "50%",
//               transform: "translateX(-50%)",
//               width: { xs: 60, sm: 80 },
//               height: 3,
//               background: `linear-gradient(90deg, ${INDIA_SAFFRON}, ${INDIA_GREEN})`,
//               borderRadius: 2,
//             }
//           }}
//         >
//            {language === "mr" ? "द्रुत प्रवेश" : "Quick Access"}
//         </Typography>
//       </Box>
//       {/* <Typography variant="h5" fontWeight={700} align="center" mb={4}>
//         {language === "mr" ? "द्रुत प्रवेश" : "Quick Access"}
//       </Typography> */}

//       <Grid container spacing={3}>
//         {items.map((item) => {
//           console.log("item123", item);
//           const IconComponent = getMuiIcon(item.iconName);
//           console.log("IconComponent__", IconComponent);

//           return (
//             <Grid item xs={12} sm={6} md={4} key={item.id}>
//               <InfoCard
//                 title={item.title}
//                 description={item.description}
//                 icon={
//                   IconComponent ? (
//                     <IconComponent
//                       sx={{ fontSize: 36, color: item.iconColor }}
//                     />
//                   ) : null
//                 }
//                 onClick={() => navigate(item.redirectPath)}
//               />
//             </Grid>
//           );
//         })}
//       </Grid>
//     </Box>
//   );
// };

// export default QuickAccess;
